import { EyeSlashIcon } from '@heroicons/react/24/outline';

import { useEvidence } from '../../api/hooks';
import type { EvidenceArtefact, EvidenceCoverage } from '../../api/types';
import { Role } from '../../auth/roles';
import { usePrincipal } from '../../auth/usePrincipal';
import { Badge } from '../../components/Badge';
import { Callout } from '../../components/Callout';
import { Card, CardHeader } from '../../components/Card';
import { EmptyState, QueryBoundary } from '../../components/states';
import { useFormatters } from '../../time/format';

/**
 * The redacted evidence pack.
 *
 * Two things this panel must not do. It must not imply the content is raw — everything here
 * has been through `evidence/redact.py`, and identities that are not the subject of the case
 * appear as pseudonyms. And it must not present an incomplete collection as a clean one: the
 * coverage block is rendered before the content, because a 403 on the mailbox sections means
 * inbox rules and forwarding were never examined, and those are the most common identity-
 * compromise persistence mechanisms.
 */
export function EvidencePanel({ caseId }: { caseId: string }) {
  const principal = usePrincipal();
  const permitted = principal.role >= Role.ANALYST;
  const query = useEvidence(caseId, permitted);
  const { precise } = useFormatters();

  if (!permitted) {
    return (
      <Card>
        <EmptyState
          icon={EyeSlashIcon}
          title="Evidence requires the IRA.Analyst role"
          detail="A viewer can see the case and the verdict, but not the collected evidence behind it. Ask an administrator to assign IRA.Analyst in Entra ID."
        />
      </Card>
    );
  }

  return (
    <QueryBoundary
      isLoading={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
      loadingLabel="Loading evidence"
    >
      {!query.data || query.data.artefacts.length === 0 ? (
        <Card>
          <EmptyState
            title="No evidence collected"
            detail="The IR collectors have not run for this case, or produced nothing."
          />
        </Card>
      ) : (
        <div className="space-y-4">
          <CoverageCard coverage={query.data.coverage} />
          {query.data.artefacts.map((artefact) => (
            <ArtefactCard
              key={`${artefact.kind}-${artefact.collected_at}`}
              artefact={artefact}
              format={precise}
            />
          ))}
        </div>
      )}
    </QueryBoundary>
  );
}

function CoverageCard({ coverage }: { coverage: EvidenceCoverage }) {
  // Nothing ran, so none of the counts or flags below describe anything. Rendering them
  // anyway is what made a case with no subject look like an Exchange permissions failure.
  if (coverage.attempted === false) {
    return (
      <Card tone="warning">
        <CardHeader
          title="Collection coverage"
          subtitle="What the collectors could and could not see"
        />
        <Callout tone="warning">
          <p className="font-medium">No collection was run for this case</p>
          <p className="mt-0.5 opacity-90">
            The case names no identity, so there was no account to audit and the collector
            was never started. Nothing was refused and Exchange was not involved — the
            mailbox, sign-in and endpoint evidence does not exist for this case. It was
            assessed on its alerts alone.
          </p>
        </Callout>
      </Card>
    );
  }

  return (
    <Card tone={coverage.complete ? undefined : 'warning'}>
      <CardHeader
        title="Collection coverage"
        subtitle="What the collectors could and could not see"
      />
      {coverage.complete ? (
        <p className="text-sm text-muted">
          Collection completed with no section errors or truncation markers.
        </p>
      ) : (
        <Callout tone="warning">
          <p className="font-medium">The collection is incomplete</p>
          <p className="mt-0.5 opacity-90">
            The analyst was told what could not be seen. Do not read silence in the evidence
            below as an absence of activity.
          </p>
        </Callout>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Badge tone={coverage.section_error_count > 0 ? 'danger' : 'neutral'}>
          {coverage.section_error_count} section errors
        </Badge>
        <Badge tone="neutral">{coverage.finding_count} findings</Badge>
        <Badge tone={coverage.exchange_connected ? 'success' : 'warning'}>
          Exchange {coverage.exchange_connected ? 'connected' : 'not connected'}
        </Badge>
        {coverage.truncation_markers.map((marker) => (
          <Badge key={marker} tone="amber">
            {marker}
          </Badge>
        ))}
      </div>
      {!coverage.exchange_connected ? (
        <p className="mt-2 text-xs text-subtle">
          Without Exchange, inbox rules and mail forwarding were not examined — the two most
          common identity-compromise persistence mechanisms.
        </p>
      ) : null}
    </Card>
  );
}

function ArtefactCard({
  artefact,
  format,
}: {
  artefact: EvidenceArtefact;
  format: (value: string) => string;
}) {
  const pseudonyms = Object.entries(artefact.pseudonyms);

  return (
    <Card>
      <CardHeader title={artefact.kind} subtitle={`Collected ${format(artefact.collected_at)}`} />

      <div className="mb-3 flex flex-wrap gap-2">
        <Badge tone="neutral" title="Every artefact is sanitised before it reaches the model">
          redacted
        </Badge>
        {artefact.trailer_removed ? (
          <Badge
            tone="amber"
            title="IR summaries end with literal containment commands as operator guidance. Removed before the model saw them."
          >
            containment trailer removed ({artefact.trailer_lines_removed} lines)
          </Badge>
        ) : null}
        {artefact.header_lines_removed > 0 ? (
          <Badge tone="neutral" title="HTTP response-header dumps stripped">
            {artefact.header_lines_removed} header lines stripped
          </Badge>
        ) : null}
        {artefact.mutating_lines > 0 ? (
          <Badge
            tone="warning"
            title="Cmdlet-shaped imperative lines were neutralised so they could not read as instructions"
          >
            {artefact.mutating_lines} cmdlet lines neutralised
          </Badge>
        ) : null}
      </div>

      {artefact.injection_markers.length > 0 ? (
        <div className="mb-3">
          <Callout tone="danger" role="alert">
            <p className="font-medium">Instruction-shaped text in this artefact</p>
            <ul className="mt-1 space-y-1">
              {artefact.injection_markers.map((marker, index) => (
                <li key={`${marker}-${index}`} className="break-all font-mono text-[11px]">
                  {marker}
                </li>
              ))}
            </ul>
          </Callout>
        </div>
      ) : null}

      {pseudonyms.length > 0 ? (
        <details className="mb-3 rounded-lg border border-line bg-sunken p-3">
          <summary className="cursor-pointer text-xs font-medium text-muted">
            Pseudonym key ({pseudonyms.length})
          </summary>
          <p className="mt-1 text-xs text-subtle">
            Identities other than the subject of this case are pseudonymised before the model
            sees them. This mapping is shown to you, not to the analyst.
          </p>
          <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
            {pseudonyms.map(([pseudonym, original]) => (
              <div key={pseudonym} className="contents">
                <dt className="font-mono text-muted">{pseudonym}</dt>
                <dd className="break-all text-body">{original}</dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}

      <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg border border-line bg-sunken p-3 font-mono text-[11px] leading-relaxed text-muted scroll-thin">
        {artefact.content}
      </pre>
    </Card>
  );
}
